/**
 * Arena session state (`backend/src/game/arena/`).
 *
 * ─── One event, one shape ───────────────────────────────────────────────────
 * The server pushes the ENTIRE phase view on every transition (`arena:state`),
 * and `GET /arena/state` returns the identical shape. So this store has no
 * per-transition handlers and no state machine of its own: it holds the last
 * view it was given and re-renders from it. A reconnect is a GET, not a replay
 * of missed events — which matters, because a queued player can sit through a
 * dropped socket, a backgrounded tab, and a backend restart, and must still
 * come back to the right screen.
 *
 * ─── Every countdown on screen is derived, never counted ────────────────────
 * `msRemaining` is a snapshot taken when the push was built. `remainingMs()`
 * subtracts the elapsed wall time since it arrived. Nothing here runs an
 * interval decrementing a number — a tab that is backgrounded gets its timers
 * throttled, and a counted-down timer would come back wrong while a derived
 * one is simply correct.
 *
 * The server is the authority regardless: it re-derives every phase from
 * stored timestamps, so a client whose clock is wrong renders a wrong
 * countdown but still cannot act before the freeze ends.
 */
import { createStore } from '../../core/state/store';
import { api } from '../../core/network/api';
import { socket } from '../../core/network/socket';
import type { ArenaState, ArenaStateView } from './arena-state.types';

const IDLE_VIEW: ArenaStateView = {
  phase: 'IDLE', queuedForMs: 0, queueSize: 0, match: null, msRemaining: 0,
};

const INITIAL_STATE: ArenaState = {
  view: IDLE_VIEW,
  receivedAt: Date.now(),
  confirmOpen: false,
  loadingMap: false,
  busy: false,
  error: null,
};

export const arenaStateStore = createStore<ArenaState>({ ...INITIAL_STATE });

let wired = false;

/**
 * Subscribes to `arena:state` and re-syncs on every reconnect.
 *
 * The reconnect handler is the important half. The in-memory matchmaking queue
 * and the lobby both live server-side; a client that reconnects without asking
 * would keep rendering whatever phase it was in when the socket dropped,
 * forever.
 */
export function connectArena(): () => void {
  if (wired) return () => {};
  wired = true;

  const onState = (view: ArenaStateView) => applyView(view);
  const onReconnect = () => { void refreshArena(); };

  socket.on('arena:state', onState);
  socket.on('connect', onReconnect);

  void refreshArena();

  return () => {
    socket.off('arena:state', onState);
    socket.off('connect', onReconnect);
    wired = false;
  };
}

function applyView(view: ArenaStateView): void {
  arenaStateStore.set((s) => ({
    ...s,
    view,
    receivedAt: Date.now(),
    busy: false,
    // Leaving the flow closes any prompt that was open for it.
    confirmOpen: view.phase === 'IDLE' ? false : s.confirmOpen,
    loadingMap: view.phase === 'IDLE' ? false : s.loadingMap,
  }));
}

/** Re-reads the authoritative phase. Safe to call any time; cheap and idempotent. */
export async function refreshArena(): Promise<void> {
  try {
    applyView(await api.get<ArenaStateView>('/arena/state'));
  } catch {
    // A failed re-sync is not worth a banner: the next push corrects it, and
    // the most common cause is a socket that reconnected before the session
    // did.
  }
}

// ─── Local UI ───────────────────────────────────────────────────────────────
/** Janeks: *"After pressing the PvP buttons, You are prompted 'enter the arena?'"* */
export function openArenaConfirm(): void {
  arenaStateStore.set((s) => ({ ...s, confirmOpen: true, error: null }));
}

export function closeArenaConfirm(): void {
  arenaStateStore.set((s) => ({ ...s, confirmOpen: false }));
}

// ─── Actions (all socket-received server-side) ──────────────────────────────
function emit(event: string): void {
  arenaStateStore.set((s) => ({ ...s, busy: true, error: null }));
  socket.emit(event);
}

/** Join the queue. Returns immediately — the player keeps playing while it waits. */
export function joinArena(): void {
  arenaStateStore.set((s) => ({ ...s, confirmOpen: false }));
  emit('arena:join');
}

export function leaveArena(): void {
  emit('arena:leave');
}

export function acceptMatch(): void {
  // The map load starts the moment the offer is accepted — see
  // `reportArenaLoaded` for why this flag is set here and not on the push.
  arenaStateStore.set((s) => ({ ...s, loadingMap: true }));
  emit('arena:accept');
}

export function declineMatch(): void {
  arenaStateStore.set((s) => ({ ...s, loadingMap: false }));
  emit('arena:decline');
}

/**
 * "My map has finished loading."
 *
 * ─── This means the MAP, not the room ───────────────────────────────────────
 * The seat does not exist yet during LOBBY — the server mints it when the
 * countdown starts, which is triggered BY this call. A client that waited to
 * join the Colyseus room before reporting in would never report at all, and
 * every duel would burn the full 30-second join window before starting
 * anyway. The symptom would be "everything works, just always 30 seconds
 * slow", which is exactly the kind of thing nobody files a bug for.
 */
export function reportArenaLoaded(): void {
  arenaStateStore.set((s) => ({ ...s, loadingMap: false }));
  socket.emit('arena:loaded');
}

// ─── Derived ────────────────────────────────────────────────────────────────
/**
 * Ms left on the current phase's deadline, right now.
 *
 * Derived from the snapshot plus elapsed wall time — see this file's header
 * for why nothing counts down.
 */
export function remainingMs(state: ArenaState = arenaStateStore.get(), now = Date.now()): number {
  if (state.view.msRemaining <= 0) return 0;
  return Math.max(0, state.view.msRemaining - (now - state.receivedAt));
}

/** How long the player has been queued, right now. */
export function queuedForMs(state: ArenaState = arenaStateStore.get(), now = Date.now()): number {
  if (state.view.phase !== 'QUEUED') return 0;
  return state.view.queuedForMs + (now - state.receivedAt);
}

/** `m:ss`, for the pending badge and the lobby. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Whole seconds remaining, for the 8-second countdown. Rounds UP so it reads 8…1, never 0 while frozen. */
export function countdownSeconds(ms: number): number {
  return Math.max(0, Math.ceil(ms / 1000));
}

export function resetArena(): void {
  arenaStateStore.set({ ...INITIAL_STATE, receivedAt: Date.now() });
}
